const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
const MINUTES_IN_MONTH = MINUTES_IN_DAY * 30;
const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365;

type DurationUnit = "years" | "months" | "weeks" | "days" | "hours" | "minutes";

type DurationFormatCtor = new (
  locale: string,
  options: { style: "long" },
) => { format(duration: Partial<Record<DurationUnit, number>>): string };

const UNIT_NAMES: Record<DurationUnit, Intl.NumberFormatOptions["unit"]> = {
  years: "year",
  months: "month",
  weeks: "week",
  days: "day",
  hours: "hour",
  minutes: "minute",
};

const UNIT_ORDER: DurationUnit[] = ["years", "months", "weeks", "days", "hours", "minutes"];

function formatDurationFallback(
  duration: Partial<Record<DurationUnit, number>>,
  locale: string,
): string {
  const parts = UNIT_ORDER.flatMap((unit) => {
    const value = duration[unit];
    const unitName = UNIT_NAMES[unit];
    if (value == null || unitName == null) return [];
    return [
      new Intl.NumberFormat(locale, {
        style: "unit",
        unit: unitName,
        unitDisplay: "long",
      }).format(value),
    ];
  });
  const ListFormat = (
    Intl as typeof Intl & {
      ListFormat?: new (
        locale: string,
        options: { type: "conjunction"; style: "long" },
      ) => { format(items: string[]): string };
    }
  ).ListFormat;
  if (ListFormat && parts.length > 1) {
    return new ListFormat(locale, { type: "conjunction", style: "long" }).format(parts);
  }
  return parts.join(", ");
}

/**
 * Format a minute count as one duration in `locale`, matching the year/month/week/day/hour/minute
 * breakdown used by the English `formatTime` helper. The result is a single phrase so translated
 * sentences can interpolate it without mixing in English unit words.
 */
export function formatLocalizedDuration(totalMinutes: number, locale: string): string {
  const value = Number.isFinite(totalMinutes) ? Math.max(0, totalMinutes) : 0;
  const duration: Partial<Record<DurationUnit, number>> = {};
  const years = Math.floor(value / MINUTES_IN_YEAR);
  const months = Math.floor((value % MINUTES_IN_YEAR) / MINUTES_IN_MONTH);
  const weeks = Math.floor((value % MINUTES_IN_MONTH) / MINUTES_IN_WEEK);
  const days = Math.floor((value % MINUTES_IN_WEEK) / MINUTES_IN_DAY);
  const hours = Math.floor((value % MINUTES_IN_DAY) / MINUTES_IN_HOUR);
  const minutes = Math.ceil(value % MINUTES_IN_HOUR);

  if (years > 0) duration.years = years;
  if (months > 0) duration.months = months;
  if (weeks > 0) duration.weeks = weeks;
  if (days > 0) duration.days = days;
  if (hours > 0) duration.hours = hours;
  if (minutes > 0 || Object.keys(duration).length === 0) duration.minutes = minutes;

  const DurationFormat = (Intl as typeof Intl & { DurationFormat?: DurationFormatCtor }).DurationFormat;
  if (DurationFormat) {
    const formatted = new DurationFormat(locale, { style: "long" }).format(duration).trim();
    if (formatted) return formatted;
  }

  return formatDurationFallback(duration, locale);
}
